import { throwApiError } from "../../../api/api";
import { employeeApi } from "../../../api/employeeApi";

export type EmployeeMe = {
  id: string;
  fullName: string;
  socialName: string | null;
  displayName: string;
  /** O portal nunca recebe o CPF inteiro — só `***.456.789-**`. */
  cpfMasked: string;
  status: "ATIVO" | "AFASTADO" | "FERIAS" | "DESLIGADO";
  isFirstLogin: boolean;
  lastLoginAt: string | null;
  currentContract: {
    id: string;
    admissionDate: string;
    contractType: string;
    employer: { id: string; legalName: string; tradeName: string | null };
    position: { id: string; name: string } | null;
    costCenter: { id: string; code: string; name: string } | null;
    store: { id: string; nomeFantasia: string } | null;
  } | null;
};

export const getEmployeeMeApi = async (): Promise<EmployeeMe> => {
  try {
    const { data } = await employeeApi.get<EmployeeMe>("/dp/me/employee");
    return data;
  } catch (error) {
    throwApiError(error, "Não foi possível carregar os seus dados.");
    throw error;
  }
};

// ── Férias e afastamentos (plano D) — somente leitura ──────────────────────

export type MyVacationSchedule = {
  id: string;
  startDate: string;
  endDate: string;
  days: number;
  abonoDays: number;
  thirteenthAdvance: boolean;
  noticeIssuedAt: string | null;
  status: "PLANEJADO" | "AVISADO" | "EM_GOZO" | "CONCLUIDO" | "CANCELADO";
};

export type MyVacationPeriod = {
  id: string;
  sequence: number;
  acquisitionStart: string;
  acquisitionEnd: string;
  concessionEnd: string;
  entitledDays: number;
  balance: number;
  status: "EM_AQUISICAO" | "DISPONIVEL" | "PARCIALMENTE_GOZADO" | "GOZADO" | "VENCIDO" | "PERDIDO";
  daysToConcessionEnd: number;
  schedules: MyVacationSchedule[];
};

export type MyLeave = {
  id: string;
  type: string;
  startDate: string;
  endDate: string | null;
  expectedEndDate: string | null;
  days: number | null;
  payer: "EMPREGADOR" | "INSS" | "NENHUM";
  status: "ATIVO" | "ENCERRADO" | "CANCELADO";
  isAutoContinuation: boolean;
  parentLeaveId: string | null;
};

export const getMyVacationsApi = async (): Promise<MyVacationPeriod[]> => {
  try {
    const { data } = await employeeApi.get<{ periods: MyVacationPeriod[] }>("/dp/me/vacations");
    return data.periods;
  } catch (error) {
    throwApiError(error, "Não foi possível carregar as suas férias.");
    throw error;
  }
};

export const getMyLeavesApi = async (): Promise<MyLeave[]> => {
  try {
    const { data } = await employeeApi.get<{ items: MyLeave[] }>("/dp/me/leaves");
    return data.items;
  } catch (error) {
    throwApiError(error, "Não foi possível carregar os seus afastamentos.");
    throw error;
  }
};

// ── Minha escala (escala mensal da loja) ───────────────────────────────────

export type MyRosterDayKind = "TRABALHO" | "FOLGA" | "FERIADO" | "FERIAS" | "AFASTAMENTO" | "LOJA_FECHADA";

export type MyRoster = {
  year: number;
  month: number;
  /** false = o DP ainda não publicou a escala deste mês. */
  published: boolean;
  stores: Array<{ id: string; name: string }>;
  days: Array<{
    date: string;
    kind: MyRosterDayKind;
    shift: "MANHA" | "NOITE" | null;
    startMinute: number | null;
    endMinute: number | null;
    rosterId: string;
  }>;
};

export type MyRosterChangeRequest = {
  id: string;
  fromDate: string;
  toDate: string;
  reason: string | null;
  status: "PENDENTE" | "APROVADO" | "REJEITADO" | "CANCELADO";
  decidedAt: string | null;
  decisionNote: string | null;
  createdAt: string;
};

export const getMyRosterApi = async (year: number, month: number): Promise<MyRoster> => {
  try {
    const { data } = await employeeApi.get<MyRoster>(`/dp/me/roster/${year}/${month}`);
    return data;
  } catch (error) {
    throwApiError(error, "Não foi possível carregar a sua escala.");
    throw error;
  }
};

export const getMyRosterChangeRequestsApi = async (): Promise<MyRosterChangeRequest[]> => {
  try {
    const { data } = await employeeApi.get<MyRosterChangeRequest[]>("/dp/me/roster-change-requests");
    return data;
  } catch (error) {
    throwApiError(error, "Não foi possível carregar os seus pedidos de troca.");
    throw error;
  }
};

export const createMyRosterChangeRequestApi = async (payload: { fromDate: string; toDate: string; reason?: string }) => {
  try {
    const { data } = await employeeApi.post("/dp/me/roster-change-requests", payload);
    return data;
  } catch (error) {
    throwApiError(error, "Não foi possível enviar o pedido de troca.");
    throw error;
  }
};

export const cancelMyRosterChangeRequestApi = async (id: string) => {
  try {
    await employeeApi.delete(`/dp/me/roster-change-requests/${id}`);
  } catch (error) {
    throwApiError(error, "Não foi possível cancelar o pedido.");
    throw error;
  }
};

// ── Contracheques (feat/contracheque-portal) — só competências fechadas ────

export type MyPayslip = {
  periodId: string;
  year: number;
  month: number;
  kind: "MENSAL" | "DECIMO_TERCEIRO_1" | "DECIMO_TERCEIRO_2";
  /** "Setembro/2026", "13º/2026 (1ª)". */
  label: string;
  paymentDate: string;
  netAmount: string;
};

export const listMyPayslipsApi = async (): Promise<MyPayslip[]> => {
  try {
    const { data } = await employeeApi.get<MyPayslip[]>("/dp/me/payslips");
    return data;
  } catch (error) {
    throwApiError(error, "Não foi possível carregar os seus contracheques.");
    throw error;
  }
};

/** PDF do contracheque (via do empregado). O erro vem como blob: a mensagem é lida de dentro dele. */
export const getMyPayslipPdfApi = async (periodId: string): Promise<Blob> => {
  try {
    const { data } = await employeeApi.get(`/dp/me/payslips/${periodId}/pdf`, { responseType: "blob" });
    return data;
  } catch (error: any) {
    const body = error?.response?.data;
    if (body instanceof Blob) {
      let message = "Não foi possível abrir o contracheque.";
      try {
        const parsed = JSON.parse(await body.text());
        if (typeof parsed?.message === "string") message = parsed.message;
      } catch {
        // corpo não é JSON: fica a mensagem padrão
      }
      throw new Error(message);
    }
    throwApiError(error, "Não foi possível abrir o contracheque.");
    throw error;
  }
};